import { usePlatform } from '@localagent/platform';
import { useShogunchop } from '../../providers/ShogunChopProvider';

/** The scheme macOS routes to the installed app, whatever realm or port it runs on. */
export const DESKTOP_SCHEME_URL = 'shogunchop://open';

/**
 * Bridge connection states, derived from the two signals the backend exposes:
 *
 *  - `offline`    — no device in the account's connection table.
 *  - `connecting` — a device is registered (active_connections) but its bridge
 *                   ConnectionWorkflow is not reporting connected yet. This is
 *                   the cold-start / warm-up window; device-routed work would
 *                   fail right now.
 *  - `connected`  — a device's workflow-backed `connected` flag is true;
 *                   device-routed work will reach the daemon.
 */
export type BridgeConnectionState = 'connected' | 'connecting' | 'offline';

export function deriveBridgeState(
  available: boolean,
  bridgeConnected: boolean,
): BridgeConnectionState {
  if (bridgeConnected) return 'connected';
  if (available) return 'connecting';
  return 'offline';
}

const PRESENTATION: Record<
  BridgeConnectionState,
  { dot: string; label: string; title: string }
> = {
  connected: {
    dot: 'bg-green-500',
    label: 'Desktop connected',
    title: 'The desktop app is connected to the bridge — device tools are available',
  },
  connecting: {
    dot: 'bg-amber-500 animate-pulse',
    label: 'Desktop connecting…',
    title:
      'The desktop app is registered but its bridge connection is not ready yet — ' +
      'device tools will be unavailable until it connects',
  },
  offline: {
    dot: 'bg-muted-foreground/40',
    label: 'No desktop',
    title: 'No desktop app is connected to the bridge for this account',
  },
};

/**
 * Presentational indicator — pure function of `state` so it can be exercised
 * across all states in a Storybook functional story without a live backend.
 * `onOpen` is the web-arm host adapter (sdk ADR-022): the pill hands off to
 * the desktop over the URL scheme. Desktop does not pass it.
 */
export function BridgeConnectionIndicatorView({
  state,
  onOpen,
}: {
  state: BridgeConnectionState;
  onOpen?: () => void;
}) {
  const p = PRESENTATION[state];
  const className = onOpen
    ? 'flex items-center gap-1.5 rounded-md px-1 -mx-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
    : 'flex items-center gap-1.5 text-xs text-muted-foreground';
  const inner = (
    <>
      <span className={`flex h-2 w-2 rounded-full ${p.dot}`} />
      <span>{p.label}</span>
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        data-testid="bridge-connection-indicator"
        data-state={state}
        className={className}
        title={`${p.title}. Click to open the desktop app.`}
        aria-label={`Open desktop app. ${p.title}`}
        onClick={onOpen}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      data-testid="bridge-connection-indicator"
      data-state={state}
      className={className}
      title={p.title}
    >
      {inner}
    </div>
  );
}

/** Container — wires the live bridge connection state into the view. */
export function BridgeConnectionIndicator({
  openScheme,
}: {
  /** Story / e2e host adapter; production assigns `window.location.href`. */
  openScheme?: (url: string) => void;
} = {}) {
  const { available, bridgeConnected } = useShogunchop();
  const platform = usePlatform();
  const onOpen =
    platform.type === 'web'
      ? () => {
          (openScheme ?? ((url: string) => { window.location.href = url; }))(DESKTOP_SCHEME_URL);
        }
      : undefined;
  return (
    <BridgeConnectionIndicatorView
      state={deriveBridgeState(available, bridgeConnected)}
      onOpen={onOpen}
    />
  );
}
